import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Moon } from "lucide-react";
import { SIMULATOR } from "../../data/content";
import { Reveal, Eyebrow } from "../motion";

const eur = (n) => `${Math.round(n).toLocaleString("fr-FR")} €`;

const FIELDS = [
  { key: "prix", label: "Prix du bien (FAI)", unit: "€", step: 1000, min: 0 },
  { key: "travaux", label: "Travaux & aménagement", unit: "€", step: 1000, min: 0 },
  { key: "apport", label: "Apport personnel", unit: "€", step: 500, min: 0 },
  { key: "taux", label: "Taux du crédit", unit: "%", step: 0.1, min: 0 },
  { key: "duree", label: "Durée du crédit", unit: "ans", step: 1, min: 1 },
  { key: "prixNuit", label: "Prix moyen par nuit", unit: "€", step: 5, min: 1 },
  { key: "nuits", label: "Nuits louées par mois", unit: "nuits", step: 1, min: 0, max: 31 },
  { key: "charges", label: "Charges mensuelles (hors crédit)", unit: "€", step: 50, min: 0 },
];

export const Simulator = () => {
  const navigate = useNavigate();
  const [v, setV] = useState(SIMULATOR.defaults);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: Number(e.target.value) || 0 }));

  const r = useMemo(() => {
    const notaire = v.prix * SIMULATOR.notaireRate;
    const total = v.prix + notaire + v.travaux;
    const capital = Math.max(0, total - v.apport);
    const t = v.taux / 100 / 12;
    const n = v.duree * 12;
    const mensualite = t > 0 ? (capital * t) / (1 - Math.pow(1 + t, -n)) : capital / n;
    const revenus = v.prixNuit * v.nuits;
    const cashflow = revenus - v.charges - mensualite;
    const nuitsCouverture = v.prixNuit > 0 ? Math.ceil((mensualite + v.charges) / v.prixNuit) : 0;
    return { notaire, total, capital, mensualite, revenus, cashflow, nuitsCouverture };
  }, [v]);

  return (
    <section id="simulateur" className="scroll-mt-20 border-y border-white/10 bg-[#1D1327] py-24 lg:py-36" data-testid="section-simulateur">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>{SIMULATOR.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-serif text-4xl leading-[1.08] text-cream sm:text-5xl lg:text-6xl">{SIMULATOR.title}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:self-end">
            <Reveal delay={0.16}>
              <p className="leading-relaxed text-white/55">{SIMULATOR.text}</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="grid gap-x-8 gap-y-7 border border-white/10 bg-white/[0.02] p-6 sm:grid-cols-2 sm:p-8" data-testid="simulateur-form">
              {FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{f.label}</span>
                  <span className="mt-2 flex items-baseline gap-3 border-b border-white/15 pb-2 transition-colors focus-within:border-neon">
                    <input
                      type="number"
                      inputMode="decimal"
                      value={v[f.key]}
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      onChange={set(f.key)}
                      data-testid={`simulateur-input-${f.key}`}
                      className="w-full bg-transparent font-serif text-2xl text-cream outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon">{f.unit}</span>
                  </span>
                </label>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="flex h-full flex-col border border-neon/30 bg-ink p-6 sm:p-8" data-testid="simulateur-resultats">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45">Estimation mensuelle</p>

              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Coût total du projet</p>
                  <p className="mt-1 font-serif text-2xl text-cream" data-testid="simulateur-total">{eur(r.total)}</p>
                  <p className="mt-1 text-[11px] text-white/35">dont notaire ≈ {eur(r.notaire)}</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Mensualité du crédit</p>
                  <p className="mt-1 font-serif text-2xl text-cream" data-testid="simulateur-mensualite">{eur(r.mensualite)}</p>
                  <p className="mt-1 text-[11px] text-white/35">{eur(r.capital)} sur {v.duree} ans</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Revenus locatifs</p>
                  <p className="mt-1 font-serif text-2xl text-cream" data-testid="simulateur-revenus">{eur(r.revenus)}</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/45">Charges + crédit</p>
                  <p className="mt-1 font-serif text-2xl text-cream">{eur(r.mensualite + v.charges)}</p>
                </div>
              </div>

              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="flex items-center gap-3">
                  <Moon className="h-4 w-4 text-neon" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                    Tout est remboursé à partir de{" "}
                    <span className="text-neon-light" data-testid="simulateur-nuits-couverture">{r.nuitsCouverture} nuits</span> louées
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5" aria-hidden="true">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <span
                      key={d}
                      className={`h-2.5 w-2.5 ${d <= Math.min(31, r.nuitsCouverture) ? "bg-white/25" : d <= v.nuits ? "bg-neon" : "border border-white/15"}`}
                    />
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-white/35">Gris : nuits qui remboursent crédit et charges · Rose : nuits de cash-flow net</p>
              </div>

              <div className="mt-8 -mx-6 bg-neon/[0.08] px-6 py-5 sm:-mx-8 sm:px-8">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">Cash-flow net estimé / mois</p>
                <p className={`mt-1 font-serif text-4xl sm:text-5xl ${r.cashflow >= 0 ? "text-neon-light" : "text-white/70"}`} data-testid="simulateur-cashflow">
                  {r.cashflow >= 0 ? "+ " : "− "}
                  {eur(Math.abs(r.cashflow))}
                </p>
              </div>

              <button onClick={() => navigate("/rendez-vous", { state: { simulation: { ...v, mensualite: Math.round(r.mensualite), total: Math.round(r.total), revenus: Math.round(r.revenus), cashflow: Math.round(r.cashflow), nuitsCouverture: r.nuitsCouverture } } })} data-testid="simulateur-cta" className="btn-primary mt-8 w-full justify-center">
                {SIMULATOR.cta} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-4 text-[11px] leading-relaxed text-white/35">{SIMULATOR.disclaimer}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
